// FILE: src/components/case-studies/learner/CaseStudyPracticeScreen.js
//
// Learner entry for Case Study Practice (PR 3): case list → intro →
// runner → completion → review, as an internal state machine. Gated by
// caseStudiesEnabled + the cert's caseStudies config; signed-in only
// (attempts need identity for resume/review).

import React, { useCallback, useEffect, useState } from 'react';
import ScreenHeader from '../../shared/ScreenHeader';
import { listCaseStudies, getCaseRuntime, startCaseAttempt } from '../../../services/caseStudyService';
import { trackEvent } from '../../../services/telemetryService';
import { getExamConfig } from '../../../config/exams';
import CaseRunner from './CaseRunner';
import CaseReviewScreen from './CaseReviewScreen';

const T = {
  line: 'var(--line, #e5e8f1)', muted: 'var(--muted, #5a6582)', ink: 'var(--ink, #1a2138)',
  brand: 'var(--brand, #2c63ff)', brandSoft: 'var(--brand-soft, #eaf0ff)',
  good: 'var(--good, #16a571)', goodSoft: 'var(--good-soft, #e6f7ef)',
  panel: 'var(--panel, #ffffff)', bg: 'var(--bg, #f6f7fb)',
};
const card = { background: T.panel, border: `1px solid ${T.line}`, borderRadius: 12, padding: 16 };

export default function CaseStudyPracticeScreen({ examId, user, onBack }) {
  const [cases, setCases] = useState(null);
  const [error, setError] = useState(null);
  const [screen, setScreen] = useState({ name: 'list' });
  const narrow = typeof window !== 'undefined' && window.innerWidth < 900;
  const label = getExamConfig(examId)?.caseStudies?.label || 'Case Study Practice';

  const reload = useCallback(() => {
    setError(null);
    listCaseStudies(examId).then(setCases).catch((e) => setError(e.message));
  }, [examId]);
  useEffect(() => { if (user) reload(); }, [user, reload]);

  async function openCase(c) {
    setError(null);
    try {
      const rt = await getCaseRuntime(c.id);
      if (rt.mode === 'resume') {
        try { trackEvent('case_resumed', 'learning', { case_id: c.id, mode: rt.attempt.mode }); } catch (_) { /* noop */ }
        setScreen({ name: 'runner', caseMeta: { id: c.id, ...c }, attempt: rt.attempt, stages: rt.stages, responses: rt.responses });
      } else {
        setScreen({ name: 'intro', caseMeta: { id: c.id, ...c }, intro: rt });
      }
    } catch (e) { setError(e.message); }
  }

  async function start(caseMeta, mode) {
    setError(null);
    try {
      const res = await startCaseAttempt(caseMeta.id, { mode });
      try { trackEvent('case_started', 'learning', { case_id: caseMeta.id, mode }); } catch (_) { /* noop */ }
      setScreen({ name: 'runner', caseMeta, attempt: { ...res.attempt, question_count: res.attempt.question_count },
        stages: res.stages, responses: [] });
    } catch (e) { setError(e.message); }
  }

  if (!user) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <ScreenHeader variant="banner" coachPose="notepad" onBack={onBack} backLabel="Back"
          eyebrow="Practice" title={label} subtitle="Sign in to analyze realistic project scenarios" />
        <div style={{ ...card, marginTop: 16 }} data-testid="case-signin-prompt">
          Case studies track your progress across stages, support resume, and keep a reviewable history —
          that needs an account. Please sign in to continue.
        </div>
      </div>
    );
  }

  if (screen.name === 'runner') {
    return (
      <div style={{ maxWidth: 1240, margin: '0 auto', padding: '0 4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '10px 0' }}>
          <button type="button" data-testid="runner-exit" onClick={() => { setScreen({ name: 'list' }); reload(); }}
            style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${T.line}`, background: T.panel, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
            ← Save & exit
          </button>
          <span style={{ fontSize: '0.8rem', color: T.muted }}>
            {screen.caseMeta.title} · {screen.attempt.mode} mode · progress is saved automatically
          </span>
        </div>
        <CaseRunner caseMeta={screen.caseMeta} attempt={screen.attempt}
          initialStages={screen.stages} initialResponses={screen.responses} narrow={narrow}
          onCompleted={(summary) => {
            try { trackEvent('case_completed', 'learning', { case_id: screen.caseMeta.id, mode: screen.attempt.mode, score_pct: summary ? Math.round(summary.score) : null }); } catch (_) { /* noop */ }
            setScreen({ name: 'done', caseMeta: screen.caseMeta, attemptId: screen.attempt.id, summary });
          }} />
      </div>
    );
  }

  if (screen.name === 'done') {
    const s = screen.summary || {};
    return (
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <ScreenHeader variant="banner" coachPose="celebrate" onBack={() => { setScreen({ name: 'list' }); reload(); }}
          backLabel="Back to cases" eyebrow="Case complete" title={screen.caseMeta.title} subtitle="Nice work — let's see how it went" />
        <div style={{ ...card, marginTop: 16, textAlign: 'center' }} data-testid="case-summary">
          <div style={{ fontSize: '2.2rem', fontWeight: 800, color: (s.score ?? 0) >= 75 ? T.good : T.ink }}>{s.score}%</div>
          <div style={{ color: T.muted, marginBottom: 14 }}>{s.correct_count} of {s.question_count} correct</div>
          <button type="button" data-testid="open-review"
            style={{ padding: '10px 22px', borderRadius: 8, border: 'none', background: T.brand, color: '#fff', fontWeight: 700, cursor: 'pointer' }}
            onClick={() => setScreen({ name: 'review', caseMeta: screen.caseMeta, attemptId: screen.attemptId })}>
            Review answers →
          </button>
        </div>
      </div>
    );
  }

  if (screen.name === 'review') {
    return (
      <div style={{ maxWidth: 980, margin: '0 auto' }}>
        <CaseReviewScreen attemptId={screen.attemptId} caseMeta={screen.caseMeta}
          onBack={() => { setScreen({ name: 'list' }); reload(); }} />
      </div>
    );
  }

  if (screen.name === 'intro') {
    const c = screen.intro.case;
    const prev = screen.intro.previous;
    return (
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <ScreenHeader variant="banner" coachPose="notepad" onBack={() => setScreen({ name: 'list' })}
          backLabel="Back to cases" eyebrow="Case briefing" title={c.title} subtitle={`You are: ${c.learner_role || 'the project lead'}`} />
        <div style={{ ...card, marginTop: 16 }} data-testid="case-intro">
          <p style={{ marginTop: 0, fontSize: '0.92rem', lineHeight: 1.55 }}>{c.summary}</p>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: '0.82rem', color: T.muted, marginBottom: 12 }}>
            <span>⏱ ~{c.estimated_minutes || '?'} min</span>
            <span>❓ {c.question_count} linked questions</span>
            <span>📖 {c.stage_count} stages</span>
            <span>📊 {c.difficulty || '—'}</span>
            <span style={{ textTransform: 'capitalize' }}>🧭 {c.delivery_approach || ''}</span>
          </div>
          <div style={{ background: T.brandSoft, borderRadius: 8, padding: '10px 12px', fontSize: '0.82rem', marginBottom: 14 }}>
            One scenario supports all {c.question_count} questions. Answers are final once submitted, and
            <strong> new case information appears as you progress</strong> — read updates before answering.
            You can save and resume anytime.
          </div>
          {prev && <div style={{ fontSize: '0.8rem', color: T.muted, marginBottom: 12 }}>Previous attempt: {prev.score}%</div>}
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" data-testid="start-practice"
              style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: T.brand, color: '#fff', fontWeight: 700, cursor: 'pointer' }}
              onClick={() => start(screen.caseMeta, 'practice')}>
              Start practice (feedback per question)
            </button>
            <button type="button" data-testid="start-exam"
              style={{ padding: '10px 20px', borderRadius: 8, border: `1px solid ${T.brand}`, background: 'transparent', color: T.brand, fontWeight: 700, cursor: 'pointer' }}
              onClick={() => start(screen.caseMeta, 'exam')}>
              Start exam mode (results at the end)
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <ScreenHeader variant="banner" coachPose="notepad" onBack={onBack} backLabel="Back to session setup"
        backTestId="case-list-back" eyebrow="Practice" title={label}
        subtitle="Analyze a realistic scenario, review artifacts, and answer a connected series of questions" />
      {error && <div style={{ ...card, marginTop: 16, color: 'var(--bad, #d64545)' }} data-testid="case-list-error">{error}</div>}
      <div style={{ marginTop: 16, display: 'grid', gap: 12 }} data-testid="learner-case-list">
        {cases === null && !error && <div style={{ ...card, color: T.muted }}>Loading cases…</div>}
        {cases !== null && cases.length === 0 && (
          <div style={{ ...card, color: T.muted }}>No case studies are published for this certification yet — check back soon.</div>
        )}
        {(cases || []).map((c) => (
          <button key={c.id} type="button" data-testid={`case-card-${c.slug}`}
            style={{ ...card, textAlign: 'left', cursor: 'pointer', display: 'block', width: '100%' }}
            onClick={() => openCase(c)}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: '0.98rem' }}>{c.title}</strong>
              {c.in_progress && <span style={{ padding: '1px 9px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700, background: T.brandSoft, color: T.brand }}>In progress — resume</span>}
              {c.previous && !c.in_progress && <span style={{ padding: '1px 9px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700, background: T.goodSoft, color: T.good }}>Completed · {c.previous.score}%</span>}
            </div>
            <div style={{ fontSize: '0.84rem', color: T.muted, margin: '6px 0' }}>{c.summary}</div>
            <div style={{ display: 'flex', gap: 14, fontSize: '0.76rem', color: T.muted }}>
              <span>⏱ ~{c.estimated_minutes || '?'} min</span>
              <span>❓ {c.question_count} questions</span>
              <span>📊 {c.difficulty || '—'}</span>
              <span style={{ textTransform: 'capitalize' }}>🧭 {c.delivery_approach || ''}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
